<template>
    <ValidationObserver ref="form" v-slot="{ invalid }">
        <div class="absolute top-0 left-0 right-0 bottom-0 w-full overflow-hidden flex items-center text-center justify-center z-50" v-show="loading"><spinner line-fg-color="#922C49" size="55" :line-size="5"></spinner></div>
        <form id="form" v-if="!loading" @submit.prevent="submitForm" class="flex flex-col h-full" autocomplete="off">
            <page-header :buttons="pageActions.buttons" :additional="pageActions.additional" @method="methodHandler">
                <template slot="title">{{ type }} Application Submission</template>
            </page-header>

            <tabs :tabs="tabs" :active="startTab" @setTab="setActiveTab" ref="tabs"></tabs>
            <div class="bg-red-200 py-2 text-sm text-red-700 text-center w-full -mt-px" role="alert" v-show="invalid && submitted" >Please complete all required fields</div>
            <div class="bg-red-200 py-2 text-sm text-red-700 text-center w-full -mt-px" v-if="errorMessage">{{ errorMessage }}</div>

            <div class="flex md:flex-row sm:flex-col flex-grow">
                <div class="md:w-3/4 sm:w-full p-4 sm:p-6 lg:px-6 bg-gray-200 border-r border-gray-400" v-show="activeTab === 'details'">
                    <ValidationObserver vid="details" :slim="true">
                        <div class="grid grid-cols-1 row-gap-5 col-gap-4 sm:grid-cols-12">
                            <input-text class="sm:col-span-2"
                                type="number"
                                label="ID"
                                placeholder="ID"
                                name="friendly_id"
                                v-model="item.friendly_id"
                                :required="false"
                                :disabled="true"
                                :readonly="true"
                            />
                            <input-select class="sm:col-span-4"
                                placeholder="Search ..."
                                label="Member"
                                name="member_id"
                                v-model="item.member_id"
                                :initialValue="initialValue.member"
                                return="value"
                                :endpoint="'members'"
                                endpointLabelField="label"
                                rules="required"
                                :required="true"
                                @input="memberSelected"/>
                            <input-select class="sm:col-span-4"
                                ref="applicationForm"
                                label="Application Form"
                                placeholder="Application Form"
                                name="application_form_id"
                                v-model="item.application_form_id"
                                :initialValue="initialValue.form"
                                return="value"
                                rules="required"
                                :required="true"
                                :disabled="!options.forms.length"
                                :options="options.forms"
                                @input="loadForm"
                                v-if="type === 'New'"
                            />
                            <div v-else class="sm:col-span-4">
                                <label class="block text-sm font-medium leading-5 text-gray-800 relative mb-1">Application Form</label>
                                <div class="flex flex-row items-center text-gray-800 border border-gray-400 px-2 text-sm" style="height: 39px;">{{ item.application_form.name }}</div>
                            </div>
                            <input-date-time class="xl:col-span-2"
                                label="Application Date"
                                placeholder="Application Date"
                                name="lodged_at"
                                v-model="item.lodged_at"
                                :initialValue="initialValue.lodged_at"
                                type="date"
                                format="dd/MM/yyyy"
                                :auto="true"
                                :required="true"
                            />
                            <input-select class="sm:col-span-4"
                                label="Supplier Type"
                                placeholder="Select Supplier Type"
                                name="supplier_type_id"
                                v-model="item.supplier_type_id"
                                :initialValue="initialValue.supplier_type"
                                return="value"
                                :required="false"
                                :options="options.supplierTypes"
                                @input="typeChanged"/>
                            <input-select class="sm:col-span-4"
                                label="Supplier"
                                placeholder="Select Supplier"
                                name="supplier_id"
                                v-model="item.supplier_id"
                                :initialValue="initialValue.supplier"
                                return="value"
                                rules="required"
                                :required="item.application_form ? item.application_form.suppliers_visible : true"
                                :options="filteredSuppliers"
                                :ref="'supplier'"
                            />
                            <input-text class="sm:col-span-4"
                                label="Other Supplier"
                                placeholder="Type In Other Supplier"
                                name="supplier_other"
                                v-model="item.supplier_other"
                                :required="false"
                            />
                            <input-text class="sm:col-span-4"
                                label="Amount"
                                placeholder="Amount"
                                name="amount"
                                v-model="item.amount"
                                rules="required"
                                :required="item.application_form ? item.application_form.amount_visible : true"
                                :dollar="true"
                            />
                            <input-text class="sm:col-span-2"
                              label="GST"
                              placeholder="GST"
                              name="gst"
                              v-model="item.gst"
                              :dollar="true"
                              tooltip="Enter the GST for this invoice/receipt, leave blank for no GST"
                            />
                            <div class="sm:col-span-2 text-center">
                                <label class="block text-sm font-medium leading-5 text-gray-800 relative">
                                    <span>Limit <font-awesome-icon class="text-xs text-black ml-1 cursor-pointer" :icon="['fas', 'question-circle']" style="margin-top: 2px; text-align: center;" v-tooltip="'Per member limit of selected form'" /></span>
                                </label>
                                <div class="mt-1 leading-9 text-gray-800 text-sm">
                                    <span v-if="selectedForm">${{ selectedForm.limit.toFixed(2).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",") }}</span>
                                    <span v-else>N/A</span>
                                </div>
                            </div>
                            <div class="sm:col-span-2 text-center">
                                <label class="block text-sm font-medium leading-5 text-gray-800 relative">
                                    <span>Remaining <font-awesome-icon class="text-xs text-black ml-1 cursor-pointer" :icon="['fas', 'question-circle']" style="margin-top: 2px; text-align: center;" v-tooltip="'Limit left after approved applications'" /></span>
                                </label>
                                <div class="mt-1 leading-9 text-sm" :class="[remaining && remaining > 0 ? 'text-green-600' : remaining && remaining < 0 ? 'text-red-500' : 'text-gray-800' ]">
                                    <span class="text-lg text-default-500" v-if="calculating">
                                        <svg aria-hidden="true" focusable="false" data-prefix="fad" data-icon="spinner-third" class="svg-inline--fa fa-spinner-third fa-w-16 fa-spin" role="img" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512"><g class="fa-group"><path class="fa-secondary" fill="currentColor" d="M478.71 364.58zm-22 6.11l-27.83-15.9a15.92 15.92 0 0 1-6.94-19.2A184 184 0 1 1 256 72c5.89 0 11.71.29 17.46.83-.74-.07-1.48-.15-2.23-.21-8.49-.69-15.23-7.31-15.23-15.83v-32a16 16 0 0 1 15.34-16C266.24 8.46 261.18 8 256 8 119 8 8 119 8 256s111 248 248 248c98 0 182.42-56.95 222.71-139.42-4.13 7.86-14.23 10.55-22 6.11z" opacity="0.4"></path><path class="fa-primary" fill="currentColor" d="M271.23 72.62c-8.49-.69-15.23-7.31-15.23-15.83V24.73c0-9.11 7.67-16.78 16.77-16.17C401.92 17.18 504 124.67 504 256a246 246 0 0 1-25 108.24c-4 8.17-14.37 11-22.26 6.45l-27.84-15.9c-7.41-4.23-9.83-13.35-6.2-21.07A182.53 182.53 0 0 0 440 256c0-96.49-74.27-175.63-168.77-183.38z"></path></g></svg>
                                    </span>
                                    <span v-else-if="remaining">${{ remaining.toFixed(2).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",") }}</span>
                                    <span v-else>N/A</span>
                                </div>
                            </div>
                            <div class="sm:col-span-2 text-center">
                                <label class="block text-sm font-medium leading-5 text-gray-800 relative">
                                    <span>Pending <font-awesome-icon class="text-xs text-black ml-1 cursor-pointer" :icon="['fas', 'question-circle']" style="margin-top: 2px; text-align: center;" v-tooltip="'Total amount of applications<br />that have not been approved'" /></span>
                                </label>
                                <div class="mt-1 leading-9 text-sm text-gray-800">
                                    <span class="text-lg text-default-500" v-if="calculating">
                                        <svg aria-hidden="true" focusable="false" data-prefix="fad" data-icon="spinner-third" class="svg-inline--fa fa-spinner-third fa-w-16 fa-spin" role="img" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512"><g class="fa-group"><path class="fa-secondary" fill="currentColor" d="M478.71 364.58zm-22 6.11l-27.83-15.9a15.92 15.92 0 0 1-6.94-19.2A184 184 0 1 1 256 72c5.89 0 11.71.29 17.46.83-.74-.07-1.48-.15-2.23-.21-8.49-.69-15.23-7.31-15.23-15.83v-32a16 16 0 0 1 15.34-16C266.24 8.46 261.18 8 256 8 119 8 8 119 8 256s111 248 248 248c98 0 182.42-56.95 222.71-139.42-4.13 7.86-14.23 10.55-22 6.11z" opacity="0.4"></path><path class="fa-primary" fill="currentColor" d="M271.23 72.62c-8.49-.69-15.23-7.31-15.23-15.83V24.73c0-9.11 7.67-16.78 16.77-16.17C401.92 17.18 504 124.67 504 256a246 246 0 0 1-25 108.24c-4 8.17-14.37 11-22.26 6.45l-27.84-15.9c-7.41-4.23-9.83-13.35-6.2-21.07A182.53 182.53 0 0 0 440 256c0-96.49-74.27-175.63-168.77-183.38z"></path></g></svg>
                                    </span>
                                    <span v-else-if="pending">${{ pending.toFixed(2).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",") }}</span>
                                    <span v-else>N/A</span>
                                </div>
                            </div>
                            <input-textarea class="sm:col-span-12"
                                label="Custom Supplier Message"
                                placeholder="Custom Supplier Message"
                                name="custom_message"
                                ref="custom_message"
                                v-model="item.custom_message"
                                :required="false" />
                            <span class="sm:col-span-4 -mt-4">
                                <input-select label=""
                                    name="template"
                                    placeholder="Insert Template"
                                    return="value"
                                    :options="options.templates"
                                    v-model="template"
                                    @input="insertTemplate"
                                    ref="template_select"
                                />
                            </span>
                        </div>
                        <h2 v-if="showForm" class="mt-4 w-full col-span-12 pb-2 border-b-2 border-charcoal-900 text-left font-medium text-charcoal-900">Form</h2>
                        <div class="grid grid-cols-1 row-gap-0 col-gap-4 row-gap-2 sm:grid-cols-12 mt-4" v-if="showForm">
                            <div v-for="(element, index) in item.submission.elements" :key="index" class="flex flex-col border-2 border-dashed cursor-pointer relative z-50 element" :class="[ 'col-span-'+element.width ]" :ref="'element_'+index">
                                <label class="block text-sm font-medium leading-5 text-gray-800 relative mb-1" :class="[element.required ? 'required' : '']" v-if="element.type !== 'heading'">{{ element.label }}</label>
                                <div class="pb-1">
                                    <template v-if="element.type === 'textInput'">
                                        <input type="text" v-if="element.mask && element.mask.visible" v-model="element.value" :mask="element.mask.value" rules="required" :placeholder="element.placeholder.visible ? element.placeholder.value : ''" class="form-input block w-full transition duration-150 ease-in-out sm:text-sm leading-none text-gray-700" />
                                        <input type="text" v-else v-model="element.value" rules="required" :placeholder="element.placeholder.visible ? element.placeholder.value : ''" class="form-input block w-full transition duration-150 ease-in-out sm:text-sm leading-none text-gray-700" />
                                    </template>
                                    <template v-if="element.type === 'longTextInput'">
                                        <textarea rows="5" v-model="element.value" :placeholder="element.placeholder.visible ? element.placeholder.value : ''" class="form-input block w-full transition duration-150 ease-in-out sm:text-sm leading-none text-gray-700" ></textarea>
                                    </template>
                                    <template v-if="element.type === 'numberInput'">
                                        <input type="number" v-model="element.value" :placeholder="element.placeholder.visible ? element.placeholder.value : ''" class="form-input block w-full transition duration-150 ease-in-out sm:text-sm leading-none text-gray-700" />
                                    </template>
                                    <template v-if="element.type === 'selectInput'">
                                        <select class="border-gray-500 border py-2 px-2 block w-full transition duration-150 ease-in-out text-sm leading-none text-gray-700 focus:outline-none" v-model="element.value">
                                            <option :value="null" v-if="element.placeholder.visible" disabled selected hidden>{{ element.placeholder.value }}</option>
                                            <option v-for="(option, optionIndex) in element.options" :value="option.value ? option.value : option.label" :key="optionIndex">{{ option.label }}</option>
                                        </select>
                                    </template>
                                    <template v-if="element.type === 'radioInput'">
                                        <div class="flex" :class="[ element.display === 'vertical' ? 'flex-col' : 'flex-row flex-wrap' ]">
                                            <label v-for="(option, optionIndex) in element.options" :key="optionIndex"
                                                class="mr-4 flex flex-row items-center cursor-pointer"
                                                :class="[ element.display === 'vertical' ? 'mb-2' : '' ]"
                                            >
                                                <input type="radio" :value="option.value" :name="element.id" v-model="element.value" class="w-5 h-5"><span class="ml-1 text-sm">{{ option.label }}</span>
                                            </label>
                                        </div>
                                    </template>
                                    <template v-if="element.type === 'checkboxInput'">
                                        <div class="flex" :class="[ element.display === 'vertical' ? 'flex-col' : 'flex-row flex-wrap' ]">
                                            <label v-for="(option, optionIndex) in element.options" :key="optionIndex"
                                                class="mr-4 flex flex-row items-center cursor-pointer"
                                                :class="[ element.display === 'vertical' ? 'mb-2' : 'w-full' ]"
                                            >
                                                <input type="checkbox" :value="option.value" :name="element.id+'[]'" v-model="element.value" class="w-4 h-4"><span class="ml-1 text-sm">{{ option.label }}</span>
                                            </label>
                                        </div>
                                    </template>
                                    <template v-if="element.type === 'fileUpload'">
                                        <div class="flex flex-col">
                                            <span v-for="(file, fileIndex) in element.value" class="text-sm flex flex-row items-center last:pb-4" :key="fileIndex">
                                                <a :href="S3URL+'/' + file.path " target="_blank" class="text-gray-800 hover:text-default-500 truncate">{{ file.filename }}</a>
                                                <button type="button" @click="removeFile(element, fileIndex)">
                                                    <svg aria-hidden="true" focusable="false" data-prefix="far" data-icon="times" class="svg-inline--fa fa-times w-6 h-6 text-red-700 ml-2" role="img" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 320 512">
                                                        <path fill="currentColor" d="M207.6 256l107.72-107.72c6.23-6.23 6.23-16.34 0-22.58l-25.03-25.03c-6.23-6.23-16.34-6.23-22.58 0L160 208.4 52.28 100.68c-6.23-6.23-16.34-6.23-22.58 0L4.68 125.7c-6.23 6.23-6.23 16.34 0 22.58L112.4 256 4.68 363.72c-6.23 6.23-6.23 16.34 0 22.58l25.03 25.03c6.23 6.23 16.34 6.23 22.58 0L160 303.6l107.72 107.72c6.23 6.23 16.34 6.23 22.58 0l25.03-25.03c6.23-6.23 6.23-16.34 0-22.58L207.6 256z"></path>
                                                    </svg>
                                                </button>
                                            </span>
                                            <file-upload name="file" :folder="'files'" :multiple="true" @uploaded="fileUploaded($event, element)"></file-upload>
                                        </div>
                                    </template>
                                    <template v-if="element.type === 'dateInput'">
                                        <input-date-time class="xl:col-span-4"
                                            :placeholder="element.placeholder.visible ? element.placeholder.value : ''"
                                            :name="element.label"
                                            v-model="element.value"
                                            :initialValue="element.value"
                                            type="date"
                                            format="dd/MM/yyyy"
                                            :auto="true"
                                            :required="element.required"/>
                                    </template>
                                    <template v-if="element.type === 'heading'">
                                        <div class="block font-bold">{{ element.label }}</div>
                                    </template>
                                </div>
                            </div>
                        </div>
                    </ValidationObserver>
                </div>
                <div class="w-3/4 px-6 bg-gray-200 border-r border-gray-400" v-show="activeTab === 'notes'">
                    <notes :id="item.id" :notes="item.notes" model="ApplicationSubmission" />
                </div>

                <div class="md:w-1/4 sm:w-full px-4 sm:px-6 lg:px-6">
                    <ValidationObserver vid="details" :slim="true">
                        <div class="mt-6 grid grid-cols-1 row-gap-5 col-gap-4 sm:grid-cols-12">
                            <input-select class="sm:col-span-12"
                                label="Application Status"
                                placeholder="Application Status"
                                name="application_status_id"
                                v-model="item.application_status_id"
                                :initialValue="initialValue.application_status"
                                return="value"
                                rules="required"
                                :required="true"
                                :options="options.statuses"/>
                            <input-select class="sm:col-span-12"
                                label="FAS Status"
                                placeholder="FAS Status"
                                name="fas_status_id"
                                v-model="item.fas_status_id"
                                :initialValue="initialValue.fas_status"
                                return="value"
                                rules="required"
                                :required="true"
                                :options="options.fas_statuses"/>
                            <input-select class="sm:col-span-12"
                                label="Supplier Status"
                                placeholder="Supplier Status"
                                name="supplier_status_id"
                                v-model="item.supplier_status_id"
                                :initialValue="initialValue.supplier_status"
                                return="value"
                                rules="required"
                                :required="true"
                                :options="options.supplier_statuses"/>
                            <input-toggle class="sm:col-span-6"
                                label="Urgent"
                                name="urgent"
                                v-model="item.urgent"
                                :required="false" />
                        </div>
                    </ValidationObserver>
                </div>
            </div>
        </form>
    </ValidationObserver>
</template>

<script>
import Vue from 'vue'
import axios from 'axios'
import FormSetup from '../../mixins/Tabs'
import FormButtons from '../../mixins/FormButtons'
import Notes from '@/components/Notes'

export default {
        name: "SubmissionForm",
        props: {
            id: {required: false},
            type: {required: true},
            return: {required: false, default: '/submissions'},
            page: {required: false, default: 'submissions'}
        },
        components: {
            Notes
        },
        mixins: [
            FormSetup,
            FormButtons
        ],
        data: function () {
            return {
                resource: 'submissions',
                prefix: '',
                pageActions: {
                    buttons: [
                        {
                            label: 'Save',
                            icon: 'save',
                            iconClass: 'text-green-600',
                            type: 'method',
                            method: 'save',
                        },
                        {
                            label: 'View',
                            icon: 'eye',
                            iconClass: 'text-blue-600',
                            type: 'method',
                            method: 'view',
                            display: this.type === "Edit"
                        },
                        {
                            label: 'Cancel',
                            icon: 'times',
                            iconClass: 'text-red-600',
                            type: 'method',
                            method: 'cancel',
                        }
                    ],
                    additional: []
                },
                tabs: [
                    {
                        name: 'Details',
                        slug: 'details'
                    },
                    {
                        name: 'Notes',
                        slug: 'notes',
                        display: !!this.id
                    },
                ],
                startTab: null,
                loading: true,
                submitted: false,
                errorMessage: '',
                showPostalAddress: 'hide',
                item: {},
                S3URL: process.env.VUE_APP_S3_URL,
                options: {
                    supplierTypes: [],
                    suppliers: [],
                    forms: [],
                    statuses: [],
                    fas_statuses: [],
                    supplier_statuses: [],
                    templates: []
                },
                forms: [],
                selectedForm: null,
                remaining: null,
                pending: null,
                calculating: false,
                showForm: false,
                typeIdLookUp: {
                    application_status_cancelled_id: '',
                    application_status_completed_id: '',
                    application_status_unsuccessful_id: '',
                    fas_application_status_closed_id: '',
                    supplier_status_closed_id: ''
                },
                templates: [],
                selectedTemplate: null,
                template: null
            }
        },
        methods: {
            insertTemplate: function (payload) {
                if (payload) {
                    this.selectedTemplate = this.templates.find(template => template.id === payload)

                    if (this.item.member && this.item.member.first){
                        this.selectedTemplate.content = this.selectedTemplate.content.replace(/{first}/g, this.item.member.first)
                    }

                    if (this.item.member && this.item.member.last){
                        this.selectedTemplate.content = this.selectedTemplate.content.replace(/{last}/g, this.item.member.last)
                    }

                    if (this.item.member && this.item.member.mobile){
                        this.selectedTemplate.content = this.selectedTemplate.content.replace(/{mobile}/g, this.item.member.mobile)
                    }

                    if (this.item.member && this.item.member.full_address){
                        this.selectedTemplate.content = this.selectedTemplate.content.replace(/{address}/g, this.item.member.full_address)
                    }

                    if (this.item.friendly_id_display){
                        this.selectedTemplate.content = this.selectedTemplate.content.replace(/{submission-id}/g, this.item.friendly_id_display)
                    }

                    if (this.item.application_status){
                        this.selectedTemplate.content = this.selectedTemplate.content.replace(/{status}/g, this.item.application_status.name)
                    }

                    if (this.item.amount){
                        this.selectedTemplate.content = this.selectedTemplate.content.replace(/{amount}/g, this.item.amount)
                    }

                    if (this.item.supplier){
                        this.selectedTemplate.content = this.selectedTemplate.content.replace(/{supplier}/g, this.item.supplier.name)
                    }

                    this.item.custom_message = this.selectedTemplate.content

                    this.$refs.custom_message.$refs.custom_message.focus()
                    this.$refs.custom_message.$refs.custom_message.blur()
                    this.$refs.custom_message.$refs.custom_message.focus()

                    this.selectedTemplate = null;
                    this.$refs.template_select.clearSelect()


                }
            },
            typeChanged: function(){
                this.$refs.supplier.clearSelect();
            },
            fileUploaded: function(payload, element){
                if (!element.value || element.value === ''){
                    element.value = payload.data.map(file => {
                        return {
                            filename: file.filename,
                            path: file.path
                        }
                    })
                } else {
                    element.value = element.value.concat(payload.data.map(file => {
                        return {
                            filename: file.filename,
                            path: file.path
                        }
                    }))
                }

                this.showForm = false

                this.$nextTick(() => {
                    this.showForm = true
                })
            },
            memberSelected: function(){
                if (this.$refs.applicationForm) {
                    this.$refs.applicationForm.clearSelect();
                }

                if (this.item && this.item.member_id){
                    this.getRemainingLimit();

                    axios.get(process.env.VUE_APP_API_URL + '/members/' + this.item.member_id + '/application-forms').then(response => {
                        this.options.forms = response.data.data.map(function (form) {
                            return { label: form.name, value: form.id };
                        });
                    });
                }
            },
            getRemainingLimit: function(){
                this.remaining = null;
                this.pending = null;

                if (this.item.application_form_id && this.item.member_id){
                    this.calculating = true;

                    axios.get(process.env.VUE_APP_API_URL + '/members/' + this.item.member_id + '/application-forms/' + this.item.application_form_id + '/limits').then(response => {
                        this.calculating = false;
                        this.remaining = response.data.data.remaining;
                        this.pending = response.data.data.pending;
                    });
                }
            },
            loadForm: function(){
                if (this.type === 'New') {
                    if (this.item.application_form_id) {
                        this.selectedForm = this.forms.filter(form => {
                            return form.id === this.item.application_form_id
                        })[0];

                        const form = this.selectedForm.form;

                        if (form && form.elements && form.elements.length > 0) {
                            form.elements.forEach(element => {
                                if (element.type === 'checkboxInput'){
                                    element.value = [];
                                } else {
                                    element.value = null;
                                }
                            });
                        }

                        this.item.submission = form;
                        this.showForm = true;
                    } else {
                        this.item.submission = null;
                        this.selectedForm = null;
                        this.showForm = false;
                    }
                    this.getRemainingLimit();
                }
            },
            getData: function () {
                this.$Progress.start();

                axios.get(process.env.VUE_APP_API_URL + '/submissions/' + this.id)
                    .then(response => {
                        this.item = response.data;
                        this.selectedForm = this.item.application_form;
                        if (this.item.submission){
                            this.showForm = true;

                            this.item.submission.elements.forEach(element => {
                                if (element.type === 'fileUpload'){
                                    if (element.value && !element.value.length) {
                                        element.value = [element.value]
                                    }
                                }
                                if (element.type === 'checkboxInput'){
                                    if (!element.value || !element.value.length) {
                                        element.value = []
                                    }
                                }
                            });
                        }
                        this.item.amount = this.item.amount.toFixed(2)

                        this.$Progress.finish();
                        this.loading = false;
                    })
                    .catch(() => {
                        this.$Progress.finish();
                        this.loading = false;
                    });
            },
            submitForm: function () {
                this.submitted = false;
                this.$refs.form.validate().then(success => {
                    let _method;
                    let _url;
                    if (!success) {
                        this.submitted = true;
                        return;
                    }
                    this.submitted = true;

                    this.$store.commit('TOGGLE_BACKDROP', {open: true});
                    this.errorMessage = '';

                    if (this.id) {
                        _url = process.env.VUE_APP_API_URL + '/submissions/' + this.id;
                        _method = 'PUT';
                    } else if (this.item.id) {
                        _url = process.env.VUE_APP_API_URL + '/submissions/' + this.item.id;
                        _method = 'PUT';
                    } else {
                        _url = process.env.VUE_APP_API_URL + '/submissions';
                        _method = 'POST';
                    }

                    axios({
                        method: _method,
                        url: _url,
                        data: this.item
                    }).then(response => {
                        if (response.data.success) {
                            this.$store.commit('TOGGLE_BACKDROP', {open: false});
                            this.$router.push(`/submissions/${response.data.data.id}/view`);

                            this.$notify({
                                group: 'main',
                                type: 'success',
                                title: 'Success',
                                text: response.data.message,
                                duration: 5000
                            });

                        } else {
                            if (response.data.message) {
                                this.errorMessage = response.data.message;
                            }
                            this.$store.commit('TOGGLE_BACKDROP', {open: false});
                        }
                    }).catch(error => {
                        if (error.response.data.message) {
                            this.errorMessage = error.response.data.message;
                        } else {
                            this.errorMessage = 'An error occurred. Please contact the System Administrator.';
                        }

                        this.$store.commit('TOGGLE_BACKDROP', {open: false});
                    });
                });
            },
            cancel: function () {
                if (this.item && this.item.member_id){
                    this.$router.push('/members/'+ this.item.member_id + '/view')
                } else {
                    this.$router.push(this.return);
                }
            },
            methodHandler: function (method) {
                this[method]();
            },
            actionHandler: function (data) {
                this[data.method](data);
            },
            removeFile: function(element, index){
                element.value.splice(index, 1);
            }
        },
        beforeMount() {
            if (this.$store.getters.forms.length === 0){
                this.$store.dispatch('GET_APPLICATION_FORMS').then(() => {
                    this.forms = this.$store.getters.forms;
                });
            } else {
                this.forms = this.$store.getters.forms;
            }

            if (this.$store.getters.applicationStatuses.length === 0){
                this.$store.dispatch('GET_APPLICATION_STATUSES').then(() => {
                    this.options.statuses = this.$store.getters.applicationStatuses.map(function (type) {
                        return { label: type.name, value: type.id };
                    });
                });
            } else {
                this.options.statuses = this.$store.getters.applicationStatuses.map(function (type) {
                    return { label: type.name, value: type.id };
                });
            }

            if (this.$store.getters.supplierStatuses.length === 0){
                this.$store.dispatch('GET_SUPPLIER_STATUSES').then(() => {
                    this.options.supplier_statuses = this.$store.getters.supplierStatuses.map(function (type) {
                        return { label: type.name, value: type.id };
                    });
                });
            } else {
                this.options.supplier_statuses = this.$store.getters.supplierStatuses.map(function (type) {
                    return { label: type.name, value: type.id };
                });
            }

            if (this.$store.getters.fasApplicationStatuses.length === 0){
                this.$store.dispatch('GET_FAS_APPLICATION_STATUSES').then(() => {
                    this.options.fas_statuses = this.$store.getters.fasApplicationStatuses.map(function (type) {
                        return { label: type.name, value: type.id };
                    });
                });
            } else {
                this.options.fas_statuses = this.$store.getters.fasApplicationStatuses.map(function (type) {
                    return { label: type.name, value: type.id };
                });
            }

            if (this.$store.getters.supplierTypes.length === 0){
                this.$store.dispatch('GET_SUPPLIER_TYPES').then(() => {
                    this.options.supplierTypes = this.$store.getters.supplierTypes.map(function (type) {
                        return { label: type.name, value: type.id };
                    });
                });
            } else {
                this.options.supplierTypes = this.$store.getters.supplierTypes.map(function (type) {
                    return { label: type.name, value: type.id };
                });
            }

            this.$store.dispatch('GET_SUPPLIERS').then(() => {
                this.options.suppliers = this.$store.getters.suppliers.map(function (supplier) {
                    return { label: supplier.name, value: supplier.id };
                });
            });

            axios.get(process.env.VUE_APP_API_URL + '/templates').then(response => {
                this.templates = response.data;
                this.options.templates = this.templates
                    .filter(template => template.type.name === 'Supplier Message')
                    .map(template => {
                    return { label: template.name, value: template.id }
                })
            })
        },
        watch: {
            options: {
                handler(options){
                    if(typeof options === 'object'){
                        if(typeof options['statuses'] === 'object'){
                            options['statuses'].forEach((status) => {
                                switch(status['label']){
                                    case 'Cancelled':
                                        this.typeIdLookUp['application_status_cancelled_id'] = status['value'];
                                        break;
                                    case 'Unsuccessful':
                                        this.typeIdLookUp['application_status_unsuccessful_id'] = status['value'];
                                        break;
                                    case 'Completed':
                                        this.typeIdLookUp['application_status_completed_id'] = status['value'];
                                        break;
                                }
                            })
                        }
                        if(typeof options['fas_statuses'] === 'object'){
                            options['fas_statuses'].forEach((status) => {
                                switch(status['label']){
                                    case 'Closed':
                                        this.typeIdLookUp['fas_application_status_closed_id'] = status['value'];
                                        break;
                                }
                            })
                        }
                        if(typeof options['supplier_statuses'] === 'object'){
                            options['supplier_statuses'].forEach((status) => {
                                switch(status['label']){
                                    case 'Closed':
                                        this.typeIdLookUp['supplier_status_closed_id'] = status['value'];
                                        break;
                                }
                            })
                        }
                    }
                },
                deep: true,
                immediate: true
            },
            isApplicationStatusCancelled: {
                handler(isApplicationStatusCancelled){
                    if(isApplicationStatusCancelled){
                        Vue.set(this.item, 'fas_status_id', this.typeIdLookUp['fas_application_status_closed_id'])
                        Vue.set(this.item, 'supplier_status_id', this.typeIdLookUp['supplier_status_closed_id'])
                    }
                },
                immediate: true
            },
            isApplicationStatusUnsuccessful: {
                handler(isApplicationStatusUnsuccessful){
                    if(isApplicationStatusUnsuccessful){
                        Vue.set(this.item, 'fas_status_id', this.typeIdLookUp['fas_application_status_closed_id'])
                        Vue.set(this.item, 'supplier_status_id', this.typeIdLookUp['supplier_status_closed_id'])
                    }
                },
                immediate: true
            }
        },
        mounted() {
            if (this.type === "New") {
                this.loading = false;
                this.item.active = true;
                this.$Progress.finish();
                // Se defaults here
            } else if (this.type === "Edit") {
                this.pageActions.additional.push({
                    label: 'Delete',
                    icon: 'trash-alt',
                    type: 'method',
                    method: 'deleteModal',
                    iconClass: 'text-red-500',
                    display: !!(this.$store.getters.permissions.indexOf('members.destroy') !== -1 || this.$store.getters.nimdarepus)
                });
                this.getData();
            }

            const params = new URLSearchParams(window.location.search)
            let windowLocationHash = window.location.hash.replace('#', '');

            if (windowLocationHash) {
                this.startTab = windowLocationHash;
            }
        },
        computed: {
            isApplicationStatusCancelled(){
                return this.item.application_status_id === this.typeIdLookUp['application_status_cancelled_id']
            },
            isApplicationStatusUnsuccessful(){
                return this.item.application_status_id === this.typeIdLookUp['application_status_unsuccessful_id']
            },
            filteredSuppliers: function(){
                if (this.item.supplier_type_id){
                    return this.$store.getters.suppliers.filter(supplier => {
                        if (supplier.types && supplier.types.length > 0) {
                            return supplier.types.find(x => x.id === this.item.supplier_type_id);
                        }
                    }).map(supplier => {
                        return { label: supplier.name, value: supplier.id }
                    });
                } else {
                    return this.options.suppliers;
                }
            },
            initialValue: function(){
                let output = {};

                if (this.item && this.item.application_status){
                    output['application_status'] = this.item.application_status_id;
                } else {
                    if (this.type === "New" && this.$store.getters.applicationStatuses && this.$store.getters.applicationStatuses.length > 0){
                        output['application_status'] = this.$store.getters.applicationStatuses.filter(status => {
                            if (status.name === 'Pending'){
                                return status.id;
                            } else {
                                return false;
                            }
                        })[0].id;
                    } else {
                        output['application_status'] =  null
                    }
                }

                if (this.item && this.item.fas_status_id){
                    output['fas_status'] = this.item.fas_status_id;
                } else {
                    if (this.type === "New" && this.$store.getters.fasApplicationStatuses && this.$store.getters.fasApplicationStatuses.length > 0){
                        output['fas_status'] = this.$store.getters.fasApplicationStatuses.filter(status => {
                            if (status.name === 'Pending'){
                                return status.id;
                            } else {
                                return false;
                            }
                        })[0].id;
                    } else {
                        output['fas_status'] =  null
                    }
                }

                if (this.item && this.item.supplier_status_id){
                    output['supplier_status'] = this.item.supplier_status_id;
                } else {
                    if (this.type === "New" && this.$store.getters.supplierStatuses && this.$store.getters.supplierStatuses.length > 0){
                        output['supplier_status'] = this.$store.getters.supplierStatuses.filter(status => {
                            if (status.name === 'Pending'){
                                return status.id;
                            } else {
                                return false;
                            }
                        })[0].id;
                    } else {
                        output['supplier_status'] =  null
                    }
                }

                if (this.item && this.item.lodged_at){
                    output['lodged_at'] = this.item.lodged_at;
                } else {
                    output['lodged_at'] = new Date().toISOString();
                }

                if (this.item && this.item.application_form_id){
                    output['form'] = this.item.application_form_id;
                } else {
                    output['form'] = null;
                }

                if (this.item && this.item.member){
                    output['member'] = { value: this.item.member.id, label: this.item.member.first + ' ' + this.item.member.last + '('+this.item.member.member_id+')' };
                } else {
                    output['member'] = null;
                }

                if (this.item && this.item.supplier){
                    output['supplier'] = { value: this.item.supplier.id, label: this.item.supplier.name };
                } else {
                    output['supplier'] = null;
                }

                if (this.item && this.item.supplier_type_id){
                    output['supplier_type'] = this.item.supplier_type_id;
                } else {
                    output['supplier_type'] = null;
                }

                return output;
            }
        }
    }
</script>

<style>
.content-editor .ck.ck-editor__main > .ck-editor__editable {
    height: 50vh;
}
</style>
